import axios from 'axios';

import { API_BASE_URL, REQUEST_TIMEOUT_MS } from './config';
import { clearTokens, getRefreshToken, setTokens } from './tokenStorage';
import type { AuthTokensDto } from './types';

/**
 * Singleton refresh manager — admin twin of
 * `packages/mobile/src/api/refresh.ts`.
 *
 * If multiple in-flight API calls receive a 401 at the same time, we
 * want them all to wait on a *single* refresh call rather than each
 * issuing their own — refresh-token rotation (one-time-use refresh
 * tokens, ../../../../docs/architecture/AUTH_ARCHITECTURE.md) means a thundering herd of refresh
 * calls would all but the first one fail, logging the admin out
 * incorrectly.
 *
 * **Critical:** we use a *bare* axios call (not the configured
 * `apiClient` instance) so the refresh request doesn't re-enter the
 * response interceptor and loop. Same defence as mobile.
 */

export type RefreshOutcome =
  | { ok: true; tokens: AuthTokensDto }
  | { ok: false; reason: 'no_refresh_token' | 'http_error' | 'network_error' };

let inFlight: Promise<RefreshOutcome> | null = null;

const failureListeners = new Set<() => void>();

/**
 * Register a callback to fire when refresh fails (so the auth store
 * can tear down the session). Returns an unsubscribe function.
 */
export const onRefreshFailure = (listener: () => void): (() => void) => {
  failureListeners.add(listener);
  return () => {
    failureListeners.delete(listener);
  };
};

const notifyFailure = (): void => {
  for (const listener of failureListeners) {
    listener();
  }
};

const doRefresh = async (): Promise<RefreshOutcome> => {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    return { ok: false, reason: 'no_refresh_token' };
  }
  try {
    const response = await axios.post<AuthTokensDto>(
      `${API_BASE_URL}/auth/refresh`,
      { refreshToken },
      { timeout: REQUEST_TIMEOUT_MS },
    );
    setTokens({
      accessToken: response.data.accessToken,
      refreshToken: response.data.refreshToken,
    });
    return { ok: true, tokens: response.data };
  } catch (err) {
    if (axios.isAxiosError(err) && err.response) {
      // HTTP error from the refresh endpoint — session is dead, drop the token.
      clearTokens();
      return { ok: false, reason: 'http_error' };
    }
    return { ok: false, reason: 'network_error' };
  }
};

/** Coalesced refresh — returns the in-flight promise if a refresh is already running. */
export const refresh = async (): Promise<RefreshOutcome> => {
  if (inFlight) return inFlight;
  inFlight = (async () => {
    try {
      const outcome = await doRefresh();
      if (!outcome.ok && outcome.reason !== 'network_error') {
        // Network errors are retryable; token errors mean session is dead.
        notifyFailure();
      }
      return outcome;
    } finally {
      inFlight = null;
    }
  })();
  return inFlight;
};

/** Test seam — reset the singleton between unit tests. */
export const _resetRefreshSingleton = (): void => {
  inFlight = null;
};
